import {random} from 'lodash';
import {Store} from 'redux';

import {globalWindow} from '../../libs/shared/helpers/src/browser/browserHelpers';
import {AppState} from '../store/appState';
import {CounterCheck, getSelectedCounterCheck} from '../store/data/countersMonitorSelectors';
import {getUser} from '../store/data/teammates/teammatesSelectors';
import {postAsync} from './http/http';

/*
 * Constants.
 */

// If a counter is invalid, it might be because we have yet to receive an update.
// We will only worry about counters that are invalid for a long time.
const baseGracePeriod = 2 * 60 * 1000;

export function setupCountersMonitor(store: Store<AppState>) {
  const initialState = store.getState();
  const user = getUser(initialState);
  counterCheckEndpoint = `${user.url}/counter_monitor`;

  store.subscribe(() => {
    const state = store.getState();
    handleCountersMonitor(state);
  });
}

/*
 * Counters monitor.
 */

let counterCheckTimeout: number | undefined;
let lastInvalidHash: string | undefined;
let counterCheckEndpoint: string | undefined;

function handleCountersMonitor(state: AppState) {
  const check = getSelectedCounterCheck(state);
  const isValid = Boolean(check && check.conversationIds.length === check.counter);

  if (!check || isValid) {
    resetCounterCheckTimeout();

    return;
  }

  // Counter is invalid, but it might be because we are waiting for a server update.
  // If this lasts for more than baseGracePeriod, report to server.
  const counterHash = getCounterCheckHash(check);
  if (lastInvalidHash !== counterHash) {
    resetCounterCheckTimeout();
    lastInvalidHash = counterHash;

    // Add some jitter so multiple open tabs will not report the counter at the same time.
    const gracePeriod = random(baseGracePeriod, 2 * baseGracePeriod);

    counterCheckTimeout = globalWindow.setInstrumentedTimeout(() => {
      resetCounterCheckTimeout();
      reportInvalidCounter(check);
    }, gracePeriod);
  }
}

function resetCounterCheckTimeout() {
  if (counterCheckTimeout) {
    globalWindow.clearTimeout(counterCheckTimeout);
  }

  counterCheckTimeout = undefined;
  lastInvalidHash = undefined;
}

// We want to report counters that are stuck.
function getCounterCheckHash(check: CounterCheck) {
  return `${check.key}-${check.counter}-${check.ground}`;
}

async function reportInvalidCounter(check: CounterCheck) {
  if (!counterCheckEndpoint) {
    return;
  }

  const body = {
    type: check.type,
    counter_type: check.visibility,
    counter: check.counter,
    ground: check.ground,
    key: check.key,
    folder: check.folder,
    tag_id: check.tagId,
    inbox_id: check.inboxId,
    conversation_ids: check.conversationIds,
  };

  try {
    await postAsync(counterCheckEndpoint, body);
  } catch (error) {
    console.log('Could not send counter check', error);
  }
}
